<!-- ProductListItem.vue -->
<template>
  <div 
    :class="[
      'group relative transition-all duration-200',
      isGrid 
        ? 'overflow-hidden rounded-lg border border-gray-700 bg-dark hover:border-primary' 
        : 'border-b border-x-0 border-t-0 border-gray-700 last:border-b-0 px-4 py-3',
      !isGrid && !product.config?.is_active ? 'opacity-60' : ''
    ]"
  >
    <!-- List View Layout -->
    <div v-if="!isGrid" class="flex items-center gap-4">
      <!-- Image -->
      <div class="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-gray-800">
        <img 
          :src="product.image" 
          :alt="product.title"
          class="h-full w-full object-cover"
        />
      </div>

      <!-- Content -->
      <div class="flex flex-1 items-center justify-between">
          <div class="space-y-1 min-w-0 max-w-[500px]">
            <h3 class="font-medium text-gray-100 line-clamp-2">{{ product.title }}</h3>
            <div class="flex items-center gap-2">
              <p class="text-sm text-gray-400">{{ product.product_type }}</p>
              <span class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium"
                :class="{
                  'bg-gray-500/20 text-gray-400': product.shopify_status === 'DRAFT',
                  'bg-red-500/20 text-red-400': product.shopify_status === 'ARCHIVED'
                }"
              >
                {{ (product.shopify_status || '').toLowerCase() }}
              </span>
            </div>
            <div v-if="product.tags?.length" class="flex flex-wrap gap-1">
            <span 
              v-for="tag in product.tags" 
              :key="tag"
              class="inline-flex items-center rounded-full bg-gray-700/50 px-2 py-0.5 text-xs text-gray-300"
            >
              {{ tag }}
            </span>
          </div>
        </div>

        <!-- Controls -->
        <div class="flex items-center gap-4">
          <!-- Available Status & Inventory -->
          <div class="flex items-center gap-2">
            <span :class="[
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
              isAvailable ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'
            ]">
              <span class="relative flex h-2 w-2">
                <span :class="[
                  'absolute inline-flex h-full w-full animate-ping rounded-full opacity-75',
                  isAvailable ? 'bg-emerald-500' : 'bg-red-500'
                ]"></span>
                <span :class="[
                  'relative inline-flex h-2 w-2 rounded-full',
                  isAvailable ? 'bg-emerald-500' : 'bg-red-500'
                ]"></span>
              </span>
              {{ isAvailable ? 'Available' : 'Out of Stock' }}
            </span>
            
            <!-- Inventory Badge -->
            <span class="inline-flex items-center gap-1 rounded-full bg-gray-700/50 px-2 py-0.5 text-xs font-medium text-gray-300">
              <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              {{ product.inventory_qty }} in stock
            </span>
          </div>

          <div class="flex items-center gap-3">
            <!-- Meta Prompt Button -->
            <button
              @click="$emit('edit-meta-prompt')"
              class="group/meta relative rounded-lg border border-gray-700 p-1.5 text-gray-400 hover:border-primary hover:text-primary"
              :class="{'border-primary/30 bg-primary/10 text-primary': product.config?.meta_prompt}"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
              </svg>
              <span class="sr-only">Edit Meta Prompt</span>
              <div class="absolute -top-2 -right-2">
                <span v-if="product.config?.meta_prompt" class="flex h-3 w-3">
                  <span class="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
                </span>
              </div>
            </button>

            <!-- Priority Input -->
            <div class="relative">
              <input
                type="number"
                v-model="priority"
                @change="updatePriority"
                class="w-16 rounded border border-gray-700 bg-dark px-2 py-1 text-sm text-gray-100"
                min="0"
                :title="'Priority: ' + priority"
              />
              <span class="absolute -top-5 right-0 text-xs text-gray-400 whitespace-nowrap">
                Priority
              </span>
            </div>

            <!-- Save Button - Always render but conditionally show -->
            <div class="w-[72px]"> <!-- Fixed width container -->
              <button
                v-show="product.isDirty"
                @click="saveProduct"
                :disabled="product.isSaving"
                class="inline-flex h-[30px] w-[72px] items-center justify-center gap-1 rounded-lg border text-sm font-medium transition-all"
                :class="[
                  product.isSaving
                    ? 'border-gray-700 bg-gray-800 text-gray-400'
                    : 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                ]"
              >
                <svg
                  v-if="product.isSaving"
                  class="h-4 w-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>{{ product.isSaving ? 'Saving' : 'Save' }}</span>
              </button>
            </div>

            <!-- Active Toggle -->
            <div class="relative group">
              <button
                @click="toggleActive"
                class="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
              :class="[
                isShopifyInactive ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                product.config?.is_active ? 'bg-emerald-500' : 'bg-gray-700'
              ]"
              role="switch"
              :aria-checked="product.config?.is_active"
              :disabled="isShopifyInactive"
              :title="isShopifyInactive ? 'Please make product active in Shopify to enable' : (product.config?.is_active ? 'Active in search' : 'Inactive in search')"
            >
              <span
                class="pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                :class="[
                  product.config?.is_active ? 'translate-x-5' : 'translate-x-0'
                ]"
              >
                <span
                  class="absolute inset-0 flex h-full w-full items-center justify-center transition-opacity"
                  :class="[
                    product.config?.is_active ? 'opacity-0 duration-100 ease-out' : 'opacity-100 duration-200 ease-in'
                  ]"
                  aria-hidden="true"
                >
                  <svg class="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                    <path d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </span>
                <span
                  class="absolute inset-0 flex h-full w-full items-center justify-center transition-opacity"
                  :class="[
                    product.config?.is_active ? 'opacity-100 duration-200 ease-in' : 'opacity-0 duration-100 ease-out'
                  ]"
                  aria-hidden="true"
                >
                  <svg class="h-3 w-3 text-emerald-500" fill="currentColor" viewBox="0 0 12 12">
                    <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                  </svg>
                </span>
              </span>
              </button>
              <div v-if="isShopifyInactive" class="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-10">
                <div class="bg-dark-secondary text-xs text-gray-300 rounded-md px-2 py-1 whitespace-nowrap border border-gray-700">
                  Product must be activated in Shopify
                  <div class="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-700"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Grid View Layout -->
    <div v-else class="flex h-full flex-col">
      <!-- Image Container -->
      <div class="relative aspect-square w-full overflow-hidden bg-gray-800">
        <img 
          :src="product.image" 
          :alt="product.title"
          :class="[
            'h-full w-full object-cover transition-opacity duration-200',
            !product.config?.is_active ? 'opacity-40' : ''
          ]"
        />
        
        <!-- Status Badges -->
        <div class="absolute right-2 top-2 flex flex-col gap-1">
          <span :class="[
            'inline-flex items-center justify-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium shadow-sm min-w-[90px]',
            isAvailable ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
          ]">
            <span class="relative flex h-2 w-2">
              <span :class="[
                'absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 bg-white'
              ]"></span>
              <span :class="[
                'relative inline-flex h-2 w-2 rounded-full bg-white'
              ]"></span>
            </span>
            {{ isAvailable ? 'Available' : 'Out of Stock' }}
          </span>
          
          <!-- Inventory Badge -->
          <span class="inline-flex items-center gap-1 rounded-full bg-gray-900/80 px-2 py-0.5 text-xs font-medium text-gray-300 shadow-sm backdrop-blur-sm">
            <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            {{ product.inventory_qty }} in stock
          </span>
        </div>

      </div>

      <!-- Content -->
      <div class="flex flex-1 flex-col p-4">
        <!-- Title and Type -->
          <div class="mb-3 flex-1 min-w-0">
            <h3 class="font-medium text-gray-100 line-clamp-2">{{ product.title }}</h3>
            <div class="mt-1 flex items-center gap-2">
              <p class="text-sm text-gray-400">{{ product.product_type }}</p>
              <span class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium"
                :class="{
                  'bg-gray-500/20 text-gray-400': product.shopify_status === 'DRAFT',
                  'bg-red-500/20 text-red-400': product.shopify_status === 'ARCHIVED'
                }"
              >
                {{ (product.shopify_status || '').toLowerCase() }}
              </span>
            </div>
          </div>

        <!-- Tags -->
        <div v-if="product.tags?.length" class="mb-3 flex flex-wrap gap-1">
          <span 
            v-for="tag in product.tags" 
            :key="tag"
            class="inline-flex items-center rounded-full bg-gray-700/50 px-2 py-0.5 text-xs text-gray-300"
          >
            {{ tag }}
          </span>
        </div>

        <!-- Controls -->
        <div class="flex items-center justify-between border-t border-gray-700 pt-3">
          <div class="flex items-center gap-3">
            <!-- Meta Prompt Button -->
            <button
              @click="$emit('edit-meta-prompt')"
              class="group/meta relative rounded-lg border border-gray-700 p-1.5 text-gray-400 hover:border-primary hover:text-primary"
              :class="{'border-primary/30 bg-primary/10 text-primary': product.config?.meta_prompt}"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
              </svg>
              <span class="sr-only">Edit Meta Prompt</span>
              <div class="absolute -top-2 -right-2">
                <span v-if="product.config?.meta_prompt" class="flex h-3 w-3">
                  <span class="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
                </span>
              </div>
            </button>

            <!-- Priority Input -->
            <div class="relative">
              <input
                type="number"
                v-model="priority"
                @change="updatePriority"
                class="w-16 rounded border border-gray-700 bg-dark px-2 py-1 text-sm text-gray-100"
                min="0"
                :title="'Priority: ' + priority"
              />
              <span class="absolute -top-5 left-0 text-xs text-gray-400 whitespace-nowrap">
                Priority
              </span>
            </div>

            <!-- Save Button - Always render but conditionally show -->
            <div class="w-[72px]"> <!-- Fixed width container -->
              <button
                v-show="product.isDirty"
                @click="saveProduct"
                :disabled="product.isSaving"
                class="inline-flex h-[30px] w-[72px] items-center justify-center gap-1 rounded-lg border text-sm font-medium transition-all"
                :class="[
                  product.isSaving
                    ? 'border-gray-700 bg-gray-800 text-gray-400'
                    : 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                ]"
              >
                <svg
                  v-if="product.isSaving"
                  class="h-4 w-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>{{ product.isSaving ? 'Saving' : 'Save' }}</span>
              </button>
            </div>
          </div>

          <!-- Active Toggle -->
          <button
            @click="toggleActive"
            class="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
            :class="[
              isShopifyInactive ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              product.config?.is_active ? 'bg-emerald-500' : 'bg-gray-700'
            ]"
            role="switch"
            :aria-checked="product.config?.is_active"
            :disabled="isShopifyInactive"
            :title="isShopifyInactive ? 'Please make product active in Shopify to enable' : (product.config?.is_active ? 'Active in search' : 'Inactive in search')"
          >
            <span
              class="pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
              :class="[
                product.config?.is_active ? 'translate-x-5' : 'translate-x-0'
              ]"
            >
              <span
                class="absolute inset-0 flex h-full w-full items-center justify-center transition-opacity"
                :class="[
                  product.config?.is_active ? 'opacity-0 duration-100 ease-out' : 'opacity-100 duration-200 ease-in'
                ]"
                aria-hidden="true"
              >
                <svg class="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                  <path d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </span>
              <span
                class="absolute inset-0 flex h-full w-full items-center justify-center transition-opacity"
                :class="[
                  product.config?.is_active ? 'opacity-100 duration-200 ease-in' : 'opacity-0 duration-100 ease-out'
                ]"
                aria-hidden="true"
              >
                <svg class="h-3 w-3 text-emerald-500" fill="currentColor" viewBox="0 0 12 12">
                  <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                </svg>
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useProductStore } from '@/stores/products'

const props = defineProps<{
  product: {
    id: string
    title: string
    product_type: string
    image: string
    shopify_status: string
    config: {
      is_active: boolean
      priority: number
      meta_prompt?: string
    }
    inventory_qty: number
    tags?: string[]
    isDirty?: boolean
    isSaving?: boolean
  }
  isGrid: boolean
}>()

const emit = defineEmits<{
  (e: 'edit-meta-prompt'): void
}>()

const productStore = useProductStore()
const isAvailable = computed(() => (props.product.inventory_qty ?? 0) > 0)
const priority = ref(props.product.config?.priority ?? 0)

const isShopifyInactive = computed(() => {
  return ['DRAFT', 'ARCHIVED'].includes(props.product.shopify_status)
})

const updatePriority = async () => {
  await productStore.updateProduct(props.product.id, {
    config: {
      ...props.product.config,
      priority: priority.value
    }
  })
}

const toggleActive = async () => {
  if (isShopifyInactive.value) return
  
  await productStore.updateProduct(props.product.id, {
    config: {
      ...props.product.config,
      is_active: !props.product.config?.is_active
    }
  })
}

const saveProduct = async () => {
  try {
    await productStore.saveProduct(props.product.id)
  } catch (error) {
    // Handle error (you might want to show a notification)
    console.error('Failed to save product:', error)
  }
}
</script> 